import type { NotificationHandler } from './notification-handler'
import type { ChannelResponse, NotificationChannelInfo } from '../notification-channel/models'
import { ShellWebSocket } from './shell-websocket'
import { isChannelResponse } from '../notification-channel/channel'
import { NotificationHandlerEventType } from './notification-handler-event'
import { CHANNEL_REFRESH_PERCENTAGE, PING_INTERVAL_MS, PONG_TIMEOUT_INTERVAL_MS } from './model'
import { getShellLogger } from '../../common/logger'
import { getRandomInt } from '../../core/helpers/math'
import { Interval, Timer } from '../notification-channel/helpers'

export class WebSocketNotificationHandler extends ShellWebSocket implements NotificationHandler {
  private pongTimeout: Timer | null = null
  private pingInterval: Interval | null = null
  private connectionTimeout: Timer | null = null

  private connectionAttemptsCount = 0
  private channelStartTimestamp = 0

  constructor(public readonly channelInfo: NotificationChannelInfo) {
    super()
  }

  public connect(): void {
    this.connectionAttemptsCount = 0
    this.keepAlive()
  }

  public delete(): void {
    this.disconnect()
    this.connectionTimeout?.stop()
  }

  public pause(): void {
    this.pongTimeout?.pause()
    this.pingInterval?.pause()
  }

  public resume(): void {
    this.pongTimeout?.stop()
    this.pingInterval?.reset()
  }

  protected onOpen(event: Event): void {
    this.connectionAttemptsCount = 0
    this.connectionTimeout?.stop()
    this.startInternalPing()
    this.emit(NotificationHandlerEventType.OPEN, event)
  }

  protected onClose(event: CloseEvent): void {
    this.pingInterval?.stop()
    this.emit(NotificationHandlerEventType.CLOSE, event)
  }

  protected onError(event: Event): void {
    this.emit(NotificationHandlerEventType.ERROR, event)
  }

  protected onMessage(event: MessageEvent<string>): void {
    let message: ChannelResponse | undefined
    try {
      message = JSON.parse(event.data)
    } catch (error) {
      getShellLogger().error('WebSocket: Invalid JSON', error)
    }

    if (!message) {
      return
    }

    if (isChannelResponse(message) && this.isPongMessage(message.correlationId)) {
      this.handlePongMessage(message.correlationId)
    }

    if (message.code < 200 || message.code > 299) {
      getShellLogger().error(`WebSocket:Message contains ErrorCode ${message.code}`, message)
    } else {
      this.emit(NotificationHandlerEventType.MESSAGE, message)
    }
  }

  private startInternalPing(): void {
    this.pingInterval = new Interval(() => {
      this.internalPing()
    }, PING_INTERVAL_MS)
  }

  private internalPing(): void {
    this.ping()
    this.checkForChannelLifeTimeUpdate()
    this.pongTimeout = new Timer(() => {
      this.handlePongMessageTimeout()
    }, PONG_TIMEOUT_INTERVAL_MS)
  }

  protected handlePongMessageTimeout() {
    this.handlePongMessage()
    getShellLogger().error('WebSocket:Pong timeout')
  }

  protected handlePongMessage(correlationId?: number) {
    this.pongTimeout?.stop()
    super.handlePongMessage(correlationId)
  }

  private checkForChannelLifeTimeUpdate() {
    const timeSpent = this.getTimeSpentSinceLastChannelLifetimeUpdate()

    if (this.channelInfo.channelLifetime * CHANNEL_REFRESH_PERCENTAGE < timeSpent) {
      this.emitUpdateChannelLifetimeEvent()
      this.channelStartTimestamp = new Date().valueOf()
    }
  }

  private keepAlive(): void {
    this.connectionAttemptsCount++

    if (this.connectionAttemptsCount > 3) {
      this.emitTooManyAttemptsEvent()
    } else {
      this.tryConnecting()
    }
  }

  private tryConnecting(): void {
    if (this.channelInfo.channelData) {
      try {
        this.internalConnect(this.channelInfo.channelData.channelURL)
        this.channelStartTimestamp = new Date().valueOf()
      } catch (error) {
        getShellLogger().error('WebSocket:Connection failed', error)
      }
    } else {
      getShellLogger().error('WebSocket:ChannelData required')
    }

    const offsetInSec = this.connectionAttemptsCount ** 2
    const min = 2 + offsetInSec
    const max = 5 + offsetInSec
    const delayInMilliseconds = getRandomInt(min, max) * 1000

    this.connectionTimeout = new Timer(() => {
      this.keepAlive()
    }, delayInMilliseconds)
  }

  private emitTooManyAttemptsEvent(): void {
    this.emit(NotificationHandlerEventType.TOO_MANY_ATTEMPTS)
  }

  private emitUpdateChannelLifetimeEvent(): void {
    this.emit(NotificationHandlerEventType.UPDATE_CHANNEL_LIFETIME)
  }

  private getTimeSpentSinceLastChannelLifetimeUpdate(): number {
    return Math.floor((new Date().valueOf() - this.channelStartTimestamp) / 1000)
  }
}

export const WebSocketNotificationHandlerFactory = (channelInfo: NotificationChannelInfo) =>
  new WebSocketNotificationHandler(channelInfo)
